import { useCallback, useState } from "react";
import { FilterAutocomplete, FilterItem } from "../users/UserFilters";
import { Button, Grid, IconButton, MenuItem, Select, TextField } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { CITY_FILTER_AUTOCOMPLETE, COUNTRY_FILTER_AUTOCOMPLETE } from "../users/users.queries";

export interface LeaderboardFilterData {
  filters: Array<{
    field: string;
    operator: string;
    value: string
  }>;
  operator: 'OR' | 'AND';
}

export const LeaderboardFilter: React.FC<any> = (props: any): JSX.Element => {
  const [filterItems, setFilterItems] = useState<Array<FilterItem>>([]);
  const [operator, setOperator] = useState<'AND' | 'OR'>('AND');
  const handleFilter = useCallback((event: any) => {
    const nonEmptyFilters = filterItems.filter((filter) => filter.field !== '');
    setFilterItems([
      ...nonEmptyFilters
    ]);
    props.onFilter({
      filters: nonEmptyFilters.map((item) => ({
        field: item.field,
        operator: item.operator,
        value: `${(typeof item.value === 'object' && !(item.value instanceof Date) && !(Array.isArray(item.value))) ? item.value.name : item.value}`
      })),
      operator,
    });
  }, [filterItems, operator, props]);

  const addEmptyCondition = useCallback((event: any) => {
    event.preventDefault();
    setFilterItems([
      ...filterItems,
      { field: '', operator: 'equals', value: '', type: '', kind: '' },
    ])
  }, [filterItems]);

  const updateFilterItem = useCallback((item: FilterItem, index: number) => {
    filterItems[index] = item;
    setFilterItems([
      ...filterItems,
    ]);
  }, [filterItems]);

  const deleteFilterItem = useCallback((index: number) => {
    setFilterItems(filterItems.filter((value, i) => i !== index));
  }, [filterItems]);

  const fields = [
    {
      name: 'username',
      type: 'String',
      kind: 'scalar',
    },
    {
      name: 'regionCountry',
      type: 'String',
      kind: 'scalar',
      isReference: true,
    },
    {
      name: 'regionCity',
      type: 'String',
      kind: 'scalar',
      isReference: true,
    },
    {
      name: 'level',
      type: 'String',
      kind: 'scalar',
    }
  ];
  return (
    <div>
      <Button onClick={addEmptyCondition}>Add new condition</Button>
      {filterItems.map((item, index) => (
        <div key={`item-${index}-${item.field}`}>
          <Grid container>
            <Grid item xs={2}>
              <Select
                value={item.field}
                onChange={(event: any) => {
                  updateFilterItem({
                    ...item,
                    field: event.target.value,
                    type: fields.find((field: any) => field.name === event.target.value)!.type,
                    kind: fields.find((field: any) => field.name === event.target.value)!.kind,
                  }, index);
                }}
              >
                {fields.map((field: any) => (<MenuItem key={field.name} value={field.name}>{field.name}</MenuItem>))}
              </Select>
            </Grid>
            <Grid item xs={2}>
              <Select
                value={item.operator}
                onChange={(event: any) => {
                  updateFilterItem({
                    ...item,
                    operator: event.target.value,
                  }, index);
                }}
              >
                <MenuItem value={'equals'}>equals</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={7}>
              {((item.field === 'username' || item.field === 'level')) && (
                <TextField
                  value={item.value}
                  onChange={(event: any) => {
                    updateFilterItem({
                      ...item,
                      value: event.target.value,
                    }, index);
                  }}
                />
              )}
              {item.field === 'regionCountry' && (<
                FilterAutocomplete
                query={COUNTRY_FILTER_AUTOCOMPLETE}
                label="Country"
                noOptionsText="No country"
                value={item.value === '' ? { displayName: '', name: '' } : item.value as { displayName: string, name: string }}
                optionExtractor={(data) => data.getCountriesAutocomplete.map((country: any) => ({
                  displayName: country.regionCountry,
                  name: country.regionCountry,
                }))}
                onChange={(value) => {
                  updateFilterItem({
                    ...item,
                    value: value,
                  }, index);
                }}
              />
              )}

              {item.field === 'regionCity' && (<
                FilterAutocomplete
                query={CITY_FILTER_AUTOCOMPLETE}
                label="City"
                noOptionsText="No city"
                value={item.value === '' ? { displayName: '', name: '' } : item.value as { displayName: string, name: string }}
                optionExtractor={(data) => data.getCitiesAutocomplete.map((city: any) => ({
                  name: city.regionCity,
                  displayName: `${city.regionCity}, ${city.regionState}, ${city.regionCountry}`,
                }))}
                onChange={(value) => {
                  updateFilterItem({
                    ...item,
                    value: value,
                  }, index);
                }}
              />
              )}
            </Grid>
            <Grid item xs={1}>
              <IconButton aria-label="delete" onClick={() => deleteFilterItem(index)}>
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </div>
      ))}
      <div>
        Operator among conditions:
        <Select
          value={operator}
          onChange={(event: any) => setOperator(event.target.value)}
        >
          <MenuItem value={'AND'}>AND</MenuItem>
          <MenuItem value={'OR'}>OR</MenuItem>
        </Select>
      </div>
      <div>
        <Button variant="contained" onClick={handleFilter}>Filter</Button>
      </div>
    </div>
  )
}